import styled from '@emotion/styled';
import Image from 'next/image';
import Link from 'next/link';
import skillupBg01Mobile from '../comm/images/skillup-bg-01-mobile.png';
import skillupBg01 from '../comm/images/skillup-bg-01.png';
import skillupBg02Mobile from '../comm/images/skillup-bg-02-mobile.png';
import skillupBg02 from '../comm/images/skillup-bg-02.png';
import { Section } from '@/components/pages/about/comm/Layout';
import useCustomBreakpoint, { breakpointMdSmall } from '@/components/pages/about/comm/lib/custom-breakpoint.service';
import { body02Medium, body03, headline04, title03 } from '@/components/pages/about/styles/typography';
import { breakpointMedium } from '@/constants/breakpoints';
import { colors } from '@/constants/colors';

const contents = [
  {
    id: 'exam',
    style: {
      backgroundColor: colors.blue25,
      accentColor: colors.primary,
      backgroundImage: (breakpoint: ReturnType<typeof useCustomBreakpoint>) =>
        breakpoint === 'sm' ? skillupBg01Mobile : skillupBg01,
    },
    text: {
      chip: '기출문제',
      title: (
        <span>
          기출문제와 모의고사 풀어보고 <br />
          부담없이 인증시험을 준비해보세요.
        </span>
      ),
      description:
        '이전 회차에서 제출된 기출문제, 다양한 유형의 모의고사를 전부 무료로 제공합니다.  LV 1부터 LV 5까지 다양한 문제를 풀어보고 인증시험을 준비해보세요.',
    },
    cta: {
      label: '모의고사 · 기출문제 바로가기',
      path: '/about/sample',
    },
  },
  {
    id: 'course',
    style: {
      backgroundColor: '#FFF7E8',
      accentColor: colors.secondaryOrange,
      backgroundImage: (breakpoint: ReturnType<typeof useCustomBreakpoint>) =>
        breakpoint === 'sm' ? skillupBg02Mobile : skillupBg02,
    },
    text: {
      chip: '교육 콘텐츠',
      title: (
        <span>
          모의고사를 풀다 막혔다면 <br />이 강의를 추천해요.
        </span>
      ),
      description:
        '초보 학습자를 위한 기초 강의부터 상급자를 위한 모의고사 해설 강의까지 준비했습니다. 스킬 역량도 자격증 합격도 놓치고 싶지 않다면, 이 강의들을 수강해보세요.',
    },
    cta: {
      label: '자격증 강의 바로가기',
      path: '/education',
    },
  },
] as const;

const SkillupSection = () => {
  const breakpoint = useCustomBreakpoint();

  return (
    <StyledSectionContainer>
      <StyledSectionInner>
        <Section.Hgroup>
          <Section.Title>
            시험 준비도 프로그래머스와 함께 탄탄하게 대비해요.
          </Section.Title>
          <Section.Subtitle>
            모의고사로 역량 진단하고, 맞춤 강의를 통해 단계별로 스킬 향상!
          </Section.Subtitle>
        </Section.Hgroup>

        <StyledList>
          {contents.map(({ id, style, text, cta }) => (
            <ContentItem
              key={id}
              backgroundColor={style.backgroundColor}
            >
              <StyledLink href={cta.path}>
                <ContentHgroup>
                  <ChipText color={style.accentColor}>{text.chip}</ChipText>
                  <ContentTitle>{text.title}</ContentTitle>
                  <ContentDescription>{text.description}</ContentDescription>
                  <ContentShortcut color={style.accentColor}>
                    {cta.label}
                    <ArrowIcon id="skillup-arrow-icon" />
                  </ContentShortcut>
                </ContentHgroup>

                <Figure>
                  <Image
                    src={style.backgroundImage(breakpoint)}
                    alt="배경 이미지"
                  />
                </Figure>
              </StyledLink>
            </ContentItem>
          ))}
        </StyledList>
      </StyledSectionInner>
    </StyledSectionContainer>
  );
};

export default SkillupSection;

const ArrowIcon = ({ id }: { id: string }) => {
  return (
    <svg
      id={id}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M16.2036 6.60781L21 11.8039L16.2036 17L14.6799 15.5935L17.2209 12.8408L3 12.8408L3 10.7672L17.221 10.7672L14.6799 8.0143L16.2036 6.60781Z"
        fill="currentColor"
      />
    </svg>

  );
};

const StyledSectionContainer = styled(Section.Container)`
padding: 80px 0px 160px 0px;

@media (max-width: ${breakpointMedium}) {
  padding: 60px 0px 120px 0px;
}
`;

const StyledSectionInner = styled(Section.Inner)`
display: flex;
flex-direction: column;
gap: 48px;
`;

const StyledList = styled(Section.List)`
flex-direction: row;
justify-content: space-between;
gap: 32px;

@media (max-width: ${breakpointMedium}) {
  flex-direction: column;
  gap: 24px;
}
`;

const StyledLink = styled(Link)`
display: flex;
height: 100%;
flex-direction: column;
justify-content: space-between;
gap: 40px;

@media (max-width: ${breakpointMedium}) {
  gap: 24px;
}
`;

const ContentItem = styled.li<{ backgroundColor: string }>`
align-self: stretch;
background-color: ${({ backgroundColor }) => backgroundColor};
border-radius: 32px;

@media (max-width: ${breakpointMdSmall}) {
  border-radius: 24px;
}

#skillup-arrow-icon {
  transition: all 0.2s ease-out;
}

&:hover {
  #skillup-arrow-icon {
    transform: translateX(8px);
  }
}
`;

const ContentHgroup = styled.hgroup`
display: flex;
flex-direction: column;
align-items: flex-start;
align-self: stretch;
padding: 48px 48px 0 48px;
gap: 16px;

@media (max-width: ${breakpointMdSmall}) {
  padding: 32px 24px 0 24px;
  gap: 8px;
}
`;

const ChipText = styled.span<{ color: string }>`
  ${body02Medium}

  color: ${({ color }) => color};
`;

const ContentTitle = styled.h3`
${headline04}
color: ${colors.textPrimary};
`;

const ContentDescription = styled.p`
${body03}
color: ${colors.textPrimary};
`;

const ContentShortcut = styled.div<{ color: string }>`
display: flex;
align-items: center;
padding: 8px 0px;
border-radius: 5px;

${title03}
color: ${({ color }) => color};
gap: 8px;
`;

const Figure = styled.figure`
  display: flex;
  width: 100%;
  justify-content: center;

  > img {
    display: block;
    width: 100%;
    max-width: 1160px;
    height: 100%;

    @media (max-width: ${breakpointMedium}) {
      max-width: 488px;
      max-height: 310px;
    }
  }
`;
